import React, { useEffect, useState, useRef } from 'react';
import './BottomSheet.css';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Handle escape key and setup native touch events
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    // Native touch event handlers for better performance and preventDefault control
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      setStartY(touch.clientY);
      setIsDragging(true);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      
      const touch = e.touches[0];
      const deltaY = touch.clientY - startY;
      
      // Only allow downward swipes
      if (deltaY > 0) {
        setDragY(deltaY);
        // Prevent default scrolling when swiping down
        e.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;
      
      // Close threshold: 100px or 30% of sheet height
      const threshold = Math.min(100, window.innerHeight * 0.3);
      
      if (dragY > threshold) {
        // Don't set isDragging false here - handleClose will do it
        handleClose();
      } else {
        // Snap back to original position
        setIsDragging(false);
        setDragY(0);
      }
    };

    if (isOpen && sheetRef.current) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when bottom sheet is open
      document.body.style.overflow = 'hidden';
      
      // Add native touch listeners with passive: false to allow preventDefault
      const sheetElement = sheetRef.current;
      sheetElement.addEventListener('touchstart', handleTouchStart, { passive: false });
      sheetElement.addEventListener('touchmove', handleTouchMove, { passive: false });
      sheetElement.addEventListener('touchend', handleTouchEnd, { passive: false });
    }

    // Always return cleanup function
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      
      // Clean up native listeners if they were added
      if (isOpen && sheetRef.current) {
        const sheetElement = sheetRef.current;
        sheetElement.removeEventListener('touchstart', handleTouchStart);
        sheetElement.removeEventListener('touchmove', handleTouchMove);
        sheetElement.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isOpen, isDragging, startY, dragY]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };



  // Placeholder functions for the close handler dependency
  const handleClose = () => {
    // Clean up drag state immediately to prevent conflicts
    setIsDragging(false);
    setDragY(0);
    setIsClosing(true);
    
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  // Mouse handlers for desktop drag (optional)
  const handleMouseDown = (e: React.MouseEvent) => {
    setStartY(e.clientY);
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const deltaY = e.clientY - startY;
    
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    
    const threshold = Math.min(100, window.innerHeight * 0.3);
    
    if (dragY > threshold) {
      // Don't set isDragging false here - handleClose will do it
      handleClose();
    } else {
      // Snap back to original position
      setIsDragging(false);
      setDragY(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={overlayRef}
      className={`bottom-sheet-overlay ${isClosing ? 'closing' : ''}`} 
      onClick={handleBackdropClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div 
        ref={sheetRef}
        className={`bottom-sheet ${className} ${isDragging ? 'dragging' : ''} ${isClosing ? 'closing' : ''}`}
        style={{
          transform: isClosing ? 'none' : `translateY(${dragY}px)`,
          transition: isDragging || isClosing ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Handle bar for visual affordance and drag target */}
        <div className="bottom-sheet-handle" />
        
        {/* Header */}
        <div className="bottom-sheet-header">
          <h2 className="bottom-sheet-title">{title}</h2>
          <button 
            className="bottom-sheet-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BottomSheet; 